import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SparklesIcon, ArrowLeftIcon, CheckCircleIcon, GiftIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';

// 声明微信登录SDK类型
declare global {
  interface Window {
    WxLogin: any;
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';

  // 登录方式开关配置
  const enableWechatLogin = process.env.REACT_APP_ENABLE_WECHAT_LOGIN === 'true';
  const enableFeishuLogin = process.env.REACT_APP_ENABLE_FEISHU_LOGIN === 'true';
  const enablePasswordLogin = process.env.REACT_APP_ENABLE_PASSWORD_LOGIN === 'true';
  const enableRegister = process.env.REACT_APP_ENABLE_REGISTER === 'true';

  // 注册赠送提示配置
  const showRegisterGift = process.env.REACT_APP_SHOW_REGISTER_GIFT === 'true';
  const registerGiftText = process.env.REACT_APP_REGISTER_GIFT_TEXT || '系统赠送您价值10元的Token，快去试试系统效果吧！';

  // 根据环境变量决定默认登录方式
  const getDefaultMode = (): 'wechat' | 'password' | 'register' | 'feishu' => {
    if (enableWechatLogin) return 'wechat';
    if (enablePasswordLogin) return 'password';
    if (enableRegister) return 'register';
    if (enableFeishuLogin) return 'feishu'; // 飞书登录单独的mode
    return 'password';
  };
  const [mode, setMode] = useState<'wechat' | 'password' | 'register' | 'feishu'>(getDefaultMode());

  // 计算启用的登录方式数量（用于判断是否显示Tab）
  const enabledLoginCount = [enableWechatLogin, enablePasswordLogin, enableRegister].filter(Boolean).length;
  const showTabSwitch = enabledLoginCount > 1;
  const [loading, setLoading] = useState(false);
  const [feishuLoading, setFeishuLoading] = useState(false);
  const [feishuCallbackLoading, setFeishuCallbackLoading] = useState(false);
  const [error, setError] = useState('');
  const wechatContainerRef = useRef<HTMLDivElement>(null);

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  });

  // 注册成功欢迎弹窗
  const [showGiftModal, setShowGiftModal] = useState(false);

  // 处理飞书OAuth回调
  useEffect(() => {
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const error = searchParams.get('error');
    const isNewUser = searchParams.get('is_new_user') === 'true';

    if (error === 'feishu_auth_failed') {
      setError('飞书登录失败，请重试');
      return;
    }

    if (accessToken && refreshToken) {
      // 显示加载状态（飞书登录返回）
      setFeishuCallbackLoading(true);
      
      // 保存token
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      
      // 延迟1秒，让用户看到"登录成功"提示
      setTimeout(() => {
        // 获取用户信息并跳转
        authApi
          .getCurrentUser()
          .then((response) => {
            localStorage.setItem('user', JSON.stringify(response.data));
            // 检查是否需要显示赠送提示（新用户）
            if (isNewUser && showRegisterGift) {
              setFeishuCallbackLoading(false);
              setShowGiftModal(true);
            } else {
              navigate(redirectTo);
            }
          })
          .catch(() => {
            navigate('/');
          });
      }, 1000);
    }
  }, [searchParams, navigate, redirectTo, showRegisterGift]);

  // 监听微信登录回调（通过postMessage从loading页面发送）
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 处理微信登录回调（loading页面发送的code）
      if (event.data && event.data.state === 'login' && event.data.code) {
        handleWechatLogin(event.data.code);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, redirectTo]);

  // 处理微信登录
  const handleWechatLogin = async (code: string) => {
    try {
      setLoading(true);
      setError('');
      
      // 调用后端接口进行登录
      const response = await authApi.wechatLogin(code);
      
      if (response.data.access_token && response.data.refresh_token) {
        // 保存token
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        
        // 获取用户信息
        const userResponse = await authApi.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(userResponse.data));
        
        // 检查是否需要显示赠送提示（新用户）
        if (response.data.is_new_user && showRegisterGift) {
          setShowGiftModal(true);
        } else {
          navigate(redirectTo);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || '微信登录失败，请重试');
      // 重新加载二维码
      if (wechatContainerRef.current) {
        wechatContainerRef.current.innerHTML = '';
        setTimeout(() => {
          if (mode === 'wechat') {
            const wxAppId = process.env.REACT_APP_WX_OPEN_APPID || '';
            if (wxAppId && window.WxLogin) {
              new window.WxLogin({
                self_redirect: true,
                id: 'login_wechat_container',
                appid: wxAppId,
                scope: 'snsapi_login',
                redirect_uri: encodeURIComponent(process.env.REACT_APP_WX_REDIRECT_URI || `${window.location.origin}/api/auth/wechat/callback`),
                state: 'login',
                style: 'black',
                href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDIwMHB4O30KLmltcG93ZXJCb3ggLnRpdGxlIHtkaXNwbGF5OiBub25lO30KLmltcG93ZXJCb3ggLmluZm8ge3dpZHRoOiAyMDBweDt9Ci5zdGF0dXNfaWNvbiB7ZGlzcGxheTogbm9uZX0KLmltcG93ZXJCb3ggLnN0YXR1cyB7dGV4dC1hbGlnbjogY2VudGVyO30K'
              });
            }
          }
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  };

  // 初始化微信登录二维码
  useEffect(() => {
    if (mode === 'wechat' && wechatContainerRef.current && window.WxLogin) {
      wechatContainerRef.current.innerHTML = '';
      
      // 从环境变量获取配置
      const wxAppId = process.env.REACT_APP_WX_OPEN_APPID || '';
      // 回调到后端接口，在 iframe 内跳转
      const redirectUri = encodeURIComponent(process.env.REACT_APP_WX_REDIRECT_URI || `${window.location.origin}/api/auth/wechat/callback`);
      
      if (wxAppId) {
        new window.WxLogin({
          self_redirect: true,  // 在 iframe 内跳转，不刷新父页面
          id: 'login_wechat_container',
          appid: wxAppId,
          scope: 'snsapi_login',
          redirect_uri: redirectUri,
          state: 'login',
          style: 'black',
          href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDIwMHB4O30gLmltcG93ZXJCb3ggLnRpdGxlIHtkaXNwbGF5OiBub25lO30gLmltcG93ZXJCb3ggLmluZm8ge3dpZHRoOiAyMDBweDt9IC5zdGF0dXNfaWNvbiB7ZGlzcGxheTogbm9uZX0gLmltcG93ZXJCb3ggLnN0YXR1cyB7dGV4dC1hbGlnbjogY2VudGVyO30gaWZyYW1lIHtoZWlnaHQ6IDI0MHB4ICFpbXBvcnRhbnQ7fQ=='
        });
      }
    }
  }, [mode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authApi.login(loginForm.username, loginForm.password);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.response?.data?.detail || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    if (registerForm.password.length < 6) {
      setError('密码长度至少6位');
      return;
    }
    setLoading(true);
    try {
      await authApi.register(registerForm.username, registerForm.password, registerForm.displayName);
      const response = await authApi.login(registerForm.username, registerForm.password);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // 检查是否需要显示赠送提示
      if (showRegisterGift) {
        setShowGiftModal(true);
      } else {
        navigate(redirectTo);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  const handleFeishuLogin = async () => {
    setFeishuLoading(true);
    setError('');
    
    const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    try {
      const response = await authApi.getFeishuAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      // 如果获取授权URL失败，直接构造URL跳转
      const feishuAuthUrl = `https://open.feishu.cn/open-apis/authen/v1/authorize?client_id=cli_a90fffd185781cd1&redirect_uri=${encodeURIComponent(apiBaseUrl + '/api/auth/feishu/callback')}&state=RANDOMSTRING`;
      window.location.href = feishuAuthUrl;
    }
  };

  const features = [
    '智能解析招标文件，快速提取关键信息',
    'AI 辅助生成专业标书目录结构',
    '逐章节智能生成高质量正文内容',
    '支持 Word / PDF 格式导出',
  ];

  return (
    <div className="min-h-screen flex items-stretch relative overflow-hidden">
      {/* 注册成功欢迎弹窗 */}
      {showGiftModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(15, 30, 60, 0.6)', backdropFilter: 'blur(8px)' }}
            onClick={() => {
              setShowGiftModal(false);
              navigate(redirectTo);
            }}
          />

          {/* 弹窗内容 */}
          <div
            className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden animate-bounce-in"
            style={{
              background: '#ffffff',
              boxShadow: '0 24px 80px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)',
            }}
          >
            {/* 顶部装饰 */}
            <div
              className="px-8 pt-8 pb-6 text-center"
              style={{
                background: 'linear-gradient(145deg, #1a3a6b 0%, #1e4d8c 50%, #0e7ab5 100%)',
              }}
            >
              {/* 礼物图标 */}
              <div
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '2px solid rgba(255,255,255,0.3)',
                }}
              >
                <GiftIcon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">欢迎加入 AI标书助手</h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                恭喜您注册成功
              </p>
            </div>

            {/* 内容区域 */}
            <div className="px-8 py-6 text-center">
              <div
                className="mb-6 p-4 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}
              >
                <p className="text-lg text-gray-700 leading-relaxed">
                  {registerGiftText}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowGiftModal(false);
                  navigate(redirectTo);
                }}
                className="w-full py-3 text-white font-semibold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                  boxShadow: '0 4px 16px rgba(30, 77, 140, 0.35)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                开始使用
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 飞书登录成功过渡页面 */}
      {feishuCallbackLoading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #1e4d8c 0%, #0e7ab5 100%)',
          }}
        >
          <div className="text-center">
            {/* 成功图标动画 */}
            <div className="mb-6 relative">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              {/* 扩散圆环动画 */}
              <div
                className="absolute inset-0 rounded-full animate-ping"
                style={{
                  background: 'rgba(255,255,255,0.3)',
                }}
              />
            </div>

            <h2 className="text-2xl font-bold text-white mb-3">飞书登录成功</h2>
            <p className="text-blue-100 text-sm">正在跳转到首页...</p>

            {/* 加载动画 */}
            <div className="mt-6 flex justify-center gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-white rounded-full animate-bounce"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 整体背景渐变 - 从左到右 */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to right, #1e4d8c 0%, #3a6ba8 40%, #5c8abd 60%, #88acd1 75%, #b4cce4 85%, #d9e7f2 92%, #e8f0f8 97%, #edf2f8 100%)',
        }}
      />

      {/* 网格背景效果 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)
          `,
          backgroundSize: '72px 72px',
        }}
      />

      {/* 左侧品牌区 */}
      <div className="hidden lg:flex lg:w-[52%] flex-col relative">
        {/* 背景装饰 */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div
            className="absolute rounded-full blur-3xl"
            style={{
              top: '-10%',
              left: '-5%',
              width: '600px',
              height: '600px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute rounded-full blur-3xl"
            style={{
              bottom: '-15%',
              right: '-5%',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }}
          />
        </div>

        {/* 内容 */}
        <div className="relative z-10 flex flex-col h-full justify-between p-14">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer w-fit"
            onClick={() => navigate('/')}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">智能标书助手</span>
          </div>

          {/* 主内容 - 垂直居中 */}
          <div className="flex flex-col items-start max-w-lg mx-auto w-full">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8"
              style={{
                background: 'rgba(255,255,255,0.15)',
                borderColor: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <SparklesIcon className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">AI 驱动 · 智能标书助手</span>
            </div>

            <h2 className="text-5xl font-black text-white leading-tight mb-6 tracking-tight">
              让标书编写
              <br />
              <span className="text-cyan-200">更高效、更智能</span>
            </h2>

            <p className="text-white text-lg leading-relaxed mb-12 opacity-90">
              一站式 AI 辅助生成标书，智能五步完成，大幅提升交付效率与专业品质。
            </p>

            <div className="space-y-4 w-full">
              {features.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0 text-cyan-200" />
                  <span className="text-white text-sm opacity-90">{f}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4 mt-12 w-full">
              {[
                { label: '效率提升', value: '80%+' },
                { label: '智能步骤', value: '5 步' },
                { label: '文件格式', value: '多格式' },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-xl p-4 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <div className="text-2xl font-bold text-white mb-1">{value}</div>
                  <div className="text-xs text-cyan-100 opacity-80">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-white text-sm opacity-50">© 2024 智能标书助手 · Powered by LLM-ToDo</p>
        </div>
      </div>

      {/* 右侧表单区 */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative">
        {/* 内容层 */}
        <div className="relative z-10 w-full max-w-md">
          {/* 移动端 logo */}
          <div
            className="lg:hidden flex items-center gap-2.5 mb-10 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1e4d8c, #0e7ab5)' }}
            >
              <SparklesIcon className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: '#1e3a5f' }}>
              智能标书助手
            </span>
          </div>

          {/* 返回首页 */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm transition-colors mb-8"
            style={{ color: '#1e3a5f' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1e4d8c')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#1e3a5f')}
          >
            <ArrowLeftIcon className="w-4 h-4" />
            返回首页
          </button>

          {/* 卡片 */}
          <div
            className="rounded-3xl p-10 border backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.95)',
              borderColor: 'rgba(255,255,255,0.8)',
              boxShadow: '0 8px 40px rgba(30, 77, 140, 0.15), 0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            {/* 标题 */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#1e293b' }}>
                {mode === 'register' ? '创建账号' : '欢迎回来'}
              </h1>
              <p className="text-sm" style={{ color: '#64748b' }}>
                {mode === 'register' ? '填写信息完成注册' : '登录您的账号以继续使用'}
              </p>
            </div>

            {/* Tab 切换 - 仅在有多种登录方式时显示 */}
            {showTabSwitch && (
            <div
              className="flex gap-2 mb-8 p-1.5 rounded-xl"
              style={{ background: 'rgba(241, 245, 249, 0.8)' }}
            >
              {/* 微信登录按钮 - 仅在启用时显示 */}
              {enableWechatLogin && (
                <button
                  onClick={() => {
                    setMode('wechat');
                    setError('');
                  }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={
                    mode === 'wechat'
                      ? {
                          background: 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                          color: '#fff',
                          boxShadow: '0 2px 12px rgba(30, 77, 140, 0.35)',
                        }
                      : { color: '#64748b' }
                  }
                >
                  微信登录
                </button>
              )}
              {/* 账号登录按钮 - 仅在启用时显示 */}
              {enablePasswordLogin && (
                <button
                  onClick={() => {
                    setMode('password');
                    setError('');
                  }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={
                    mode === 'password'
                      ? {
                          background: 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                          color: '#fff',
                          boxShadow: '0 2px 12px rgba(30, 77, 140, 0.35)',
                        }
                      : { color: '#64748b' }
                  }
                >
                  账号登录
                </button>
              )}
              {/* 注册按钮 - 仅在启用时显示 */}
              {enableRegister && (
                <button
                  onClick={() => {
                    setMode('register');
                    setError('');
                  }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={
                    mode === 'register'
                      ? {
                          background: 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                          color: '#fff',
                          boxShadow: '0 2px 12px rgba(30, 77, 140, 0.35)',
                        }
                      : { color: '#64748b' }
                  }
                >
                  注册
                </button>
              )}
            </div>
            )}

            {/* 错误提示 */}
            {error && (
              <div
                className="mb-6 px-4 py-3 rounded-xl text-sm"
                style={{
                  background: 'rgba(254, 242, 242, 0.9)',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                }}
              >
                {error}
              </div>
            )}

            {/* 微信扫码登录 - 仅在启用时显示 */}
            {mode === 'wechat' && enableWechatLogin && (
              <div className="flex flex-col items-center">
                {process.env.REACT_APP_WX_OPEN_APPID ? (
                  <div className="w-full flex flex-col items-center" style={{ height: '260px', paddingTop: '10px' }}>
                    {/* 二维码区域 - 微信JS会自动填充内容（包括标题、二维码、头像、按钮等） */}
                    <div
                      ref={wechatContainerRef}
                      id="login_wechat_container"
                      className="w-full flex items-start justify-center"
                      style={{ height: '250px', maxHeight: '250px', overflow: 'hidden' }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg className="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/>
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500">微信登录未配置</p>
                    <p className="text-xs text-gray-400 mt-1">请联系管理员配置微信开放平台AppID</p>
                  </div>
                )}
              </div>
            )}

            {/* 账号密码登录 - 仅在启用时显示 */}
            {mode === 'password' && enablePasswordLogin && (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2.5" style={{ color: '#334155' }}>
                    用户名
                  </label>
                  <input
                    type="text"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(248, 250, 252, 0.8)',
                      border: '1.5px solid rgba(226, 232, 240, 0.8)',
                      color: '#1e293b',
                    }}
                    placeholder="请输入用户名"
                    required
                    autoFocus
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.background = 'rgba(239, 246, 255, 0.9)';
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                      e.target.style.background = 'rgba(248, 250, 252, 0.8)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2.5" style={{ color: '#334155' }}>
                    密码
                  </label>
                  <input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(248, 250, 252, 0.8)',
                      border: '1.5px solid rgba(226, 232, 240, 0.8)',
                      color: '#1e293b',
                    }}
                    placeholder="请输入密码"
                    required
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.background = 'rgba(239, 246, 255, 0.9)';
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                      e.target.style.background = 'rgba(248, 250, 252, 0.8)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
                  style={{
                    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                    boxShadow: loading ? 'none' : '0 4px 20px rgba(30, 77, 140, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? '登录中...' : '登录'}
                </button>
              </form>
            )}

            {/* 注册表单 - 仅在启用时显示 */}
            {mode === 'register' && enableRegister && (
              <form onSubmit={handleRegister} className="space-y-4">
                {[
                  { label: '用户名', key: 'username', placeholder: '3-50个字符', type: 'text', min: 3, max: 50 },
                  { label: '显示名称', key: 'displayName', placeholder: '您的昵称', type: 'text', max: 100 },
                  { label: '密码', key: 'password', placeholder: '至少6位', type: 'password', min: 6 },
                  { label: '确认密码', key: 'confirmPassword', placeholder: '再次输入密码', type: 'password' },
                ].map(({ label, key, placeholder, type, min, max }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#334155' }}>
                      {label}
                    </label>
                    <input
                      type={type}
                      value={registerForm[key as keyof typeof registerForm]}
                      onChange={(e) => setRegisterForm({ ...registerForm, [key]: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                      style={{
                        background: 'rgba(248, 250, 252, 0.8)',
                        border: '1.5px solid rgba(226, 232, 240, 0.8)',
                        color: '#1e293b',
                      }}
                      placeholder={placeholder}
                      required
                      minLength={min}
                      maxLength={max}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.background = 'rgba(239, 246, 255, 0.9)';
                        e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                        e.target.style.background = 'rgba(248, 250, 252, 0.8)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{
                    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                    boxShadow: loading ? 'none' : '0 4px 20px rgba(30, 77, 140, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? '注册中...' : '注册并登录'}
                </button>
              </form>
            )}

            {/* 飞书登录 - 独立显示区域 */}
            {mode === 'feishu' && enableFeishuLogin && (
              <div className="flex flex-col items-center py-6">
                <div className="text-center mb-8">
                  <p className="text-sm" style={{ color: '#64748b' }}>
                    点击下方按钮使用飞书账号登录
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleFeishuLogin}
                  disabled={feishuLoading}
                  className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: feishuLoading ? 'rgba(241, 245, 249, 0.8)' : 'rgba(255,255,255,0.9)',
                    border: '1.5px solid rgba(226, 232, 240, 0.8)',
                    color: feishuLoading ? '#94a3b8' : '#1e293b',
                  }}
                  onMouseEnter={(e) => {
                    if (!feishuLoading) {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.background = 'rgba(239, 246, 255, 0.9)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!feishuLoading) {
                      e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                    }
                  }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 48 48" fill="currentColor">
                    <path d="M20 4H8C5.79086 4 4 5.79086 4 8V20C4 22.2091 5.79086 24 8 24H20C22.2091 24 24 22.2091 24 20V8C24 5.79086 22.2091 4 20 4Z" />
                    <path d="M20 28H8C5.79086 28 4 29.7909 4 32V40C4 42.2091 5.79086 44 8 44H20C22.2091 44 24 42.2091 24 40V32C24 29.7909 22.2091 28 20 28Z" />
                    <path d="M40 4H28C25.7909 4 24 5.79086 24 8V20C24 22.2091 25.7909 24 28 24H40C42.2091 24 44 22.2091 44 20V8C44 5.79086 42.2091 4 40 4Z" />
                    <path d="M40 28H28C25.7909 28 24 29.7909 24 32V40C24 42.2091 25.7909 44 28 44H40C42.2091 44 44 42.2091 44 40V32C44 29.7909 42.2091 28 40 28Z" />
                  </svg>
                  {feishuLoading ? '跳转中...' : '飞书登录'}
                </button>
              </div>
            )}

            {/* 第三方登录分隔线和飞书登录 - 在其他登录方式下方显示 */}
            {(mode === 'password' || mode === 'wechat') && enableFeishuLogin && (
            <>
              <div className="relative my-7">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full" style={{ borderTop: '1px solid rgba(226, 232, 240, 0.8)' }}></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span
                    className="px-4 py-1"
                    style={{ background: 'rgba(255,255,255,0.95)', color: '#94a3b8' }}
                  >
                    或使用第三方登录
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleFeishuLogin}
                disabled={feishuLoading}
                className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: feishuLoading ? 'rgba(241, 245, 249, 0.8)' : 'rgba(255,255,255,0.9)',
                  border: '1.5px solid rgba(226, 232, 240, 0.8)',
                  color: feishuLoading ? '#94a3b8' : '#1e293b',
                }}
                onMouseEnter={(e) => {
                  if (!feishuLoading) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.background = 'rgba(239, 246, 255, 0.9)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!feishuLoading) {
                    e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                  }
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 48 48" fill="currentColor">
                  <path d="M20 4H8C5.79086 4 4 5.79086 4 8V20C4 22.2091 5.79086 24 8 24H20C22.2091 24 24 22.2091 24 20V8C24 5.79086 22.2091 4 20 4Z" />
                  <path d="M20 28H8C5.79086 28 4 29.7909 4 32V40C4 42.2091 5.79086 44 8 44H20C22.2091 44 24 42.2091 24 40V32C24 29.7909 22.2091 28 20 28Z" />
                  <path d="M40 4H28C25.7909 4 24 5.79086 24 8V20C24 22.2091 25.7909 24 28 24H40C42.2091 24 44 22.2091 44 20V8C44 5.79086 42.2091 4 40 4Z" />
                  <path d="M40 28H28C25.7909 28 24 29.7909 24 32V40C24 42.2091 25.7909 44 28 44H40C42.2091 44 44 42.2091 44 40V32C44 29.7909 42.2091 28 40 28Z" />
                </svg>
                {feishuLoading ? '跳转中...' : '飞书登录'}
              </button>
            </>
            )}
          </div>

          <p className="text-center text-xs mt-6" style={{ color: '#64748b' }}>
            登录即表示您同意我们的服务条款和隐私政策
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
